import { type Scene } from '@/designs-dev/scenes';

// The provider's side of the blurred-profile paywall — everything that happens
// after the care request email lands in their inbox.
//
// The frames already existed inside the exploration writeup, which is the wrong
// place to click through them: a writeup is read top to bottom, and what a
// provider actually meets is one screen at a time. Same scenes, on a stage, so
// the journey can be walked rather than scrolled.
//
// Ordered the way a provider meets them, then the ways it ends early. The frames
// themselves are sketch fidelity and their forms are static — they show a state
// rather than working input, which is what the scene select is for.
export type ProviderFrame =
  | 'email'
  | 'paywall'
  | 'answers'
  | 'answersReturning'
  | 'unlocked'
  | 'declined'
  | 'timeout'
  | 'failed';

export const providerScenes: Scene<{ frame: ProviderFrame }>[] = [
  {
    id: 'email',
    label: 'The email lands',
    kind: 'happy',
    repro: 'The care request in the provider’s inbox.',
    initial: { frame: 'email' },
  },
  {
    id: 'paywall',
    label: 'Blurred profile',
    kind: 'happy',
    repro:
      'The paywall, from the link in the email. Visible where it judges fit, blurred where the PII is.',
    initial: { frame: 'paywall' },
  },
  {
    id: 'answers',
    label: 'Answering the request',
    kind: 'happy',
    repro: 'The questions and the payment on one screen.',
    initial: { frame: 'answers' },
  },
  {
    id: 'answers-returning',
    label: 'Answering, second time on',
    kind: 'happy',
    repro: 'The same page on a second lead. No contact fields, no terms.',
    initial: { frame: 'answersReturning' },
  },
  {
    id: 'unlocked',
    label: 'Unlocked',
    kind: 'happy',
    repro: 'After payment: client contact and the case manager.',
    initial: { frame: 'unlocked' },
  },
  {
    id: 'declined',
    label: 'Can’t take this one',
    kind: 'happy',
    repro: 'The no, answered. No reason asked for.',
    initial: { frame: 'declined' },
  },
  {
    id: 'timeout',
    label: 'Lead has closed',
    kind: 'edge',
    repro: 'The link aged out, or the care was arranged. One screen for both.',
    initial: { frame: 'timeout' },
  },
  {
    id: 'failed',
    label: 'Something went wrong',
    kind: 'error',
    repro: 'Anything that breaks between the pay button and Stripe confirming.',
    initial: { frame: 'failed' },
  },
];
